import React, { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import SupportMessageList from "./components/SupportMessageList";
import {
  CONVERSATION_STATUS,
  CONNECTION_STATUS,
  SESSION_INIT_STATE,
  ROUTING_ATTRIBUTES,
  TIMER_CONFIG
} from './services/constants';
import {
  // Existing actions
  chatMessageSend,
  chatMessageUpdateStatus,
  setChatUserWriting,
  closeChat,
  setItems,
  setChat,
  openChat,
  // Salesforce actions
  initializeSalesforce,
  startSalesforceConversation,
  resumeSalesforceConversation,
  sendSalesforceMessage,
  sendSalesforceTypingIndicator,
  updateSalesforceStatus,
  setSalesforceConversationStatus,
  setSalesforceSessionInitState,
  setSalesforceUserContext,
  // Selectors
  selectSalesforceIsInitialized,
  selectSalesforceIsReady,
  selectSalesforceSessionInitState,
  selectSalesforceConversationStatus,
  selectSalesforceConnectionStatus,
  selectSalesforceCanSendMessage,
  selectSalesforceUserContext,
  // Agent wait timer actions and selectors
  resetAgentWaitState,
  startAgentWaitTimerAsync,
  stopAgentWaitTimerAsync,
  selectHasAgentTimedOut,
  selectIsWaitingForAgent,
  // Network monitoring
  setIsNetworkOnline,
  selectIsNetworkOnline,
} from "./redux/messageSlice";
import { isSalesforceEnabled, getRoutingAttributes } from "./config/salesforceConfig";
import SALESFORCE_CONFIG from "./config/salesforceConfig";
import networkMonitorService from './services/networkMonitorService';
import "Modules/chat/index.scss";
import { SUPPORT_CHAT_NETWORK_LISTENER_ID } from "./types/constants";

/**
 * ============================================================================
 * Salesforce SUPPORT CHAT - CONVERSATION FLOW DIAGRAM
 * ============================================================================
 * 
 *                        ┌──────────────────────────┐
 *                        │  User Opens Chat Dialog  │
 *                        └────────────┬─────────────┘
 *                                     │
 *                    ┌────────────────▼────────────────────────┐
 *                    │ 1️⃣ CHECK: initializationStateRef       │
 *                    │    (What's the session state?)         │
 *                    └────────────────┬────────────────────────┘
 *                                     │
 *                ┌────────────────────┼────────────────────┐
 *                │                    │                    │
 *                ▼                    ▼                    ▼
 *    ┌──────────────────┐  ┌──────────────────┐  ┌──────────────────┐
 *    │  'initialized'   │  │  'initializing'  │  │  'not-started'   │
 *    │ (Already loaded) │  │ (In progress)    │  │  (Need to init)  │
 *    └──────────┬───────┘  └──────────┬───────┘  └──────────┬───────┘
 *               │                     │                      │
 *         ✅ SKIP (instant)      ⏳ SKIP (guard)            │
 *               │                     │           ┌──────────▼─────────────┐
 *               │                     │           │ 2️⃣ CHECK localStorage: │
 *               │                     │           │    - conversationId?    │
 *               │                     │           │    - JWT token?         │
 *               │                     │           └──────────┬─────────────┘
 *               │                     │                      │
 *               │                     │         ┌────────────┴───────────────┐
 *               │                     │         │                            │
 *               │                     │         ▼                            ▼
 *               │                     │  ┌──────────────────┐  ┌──────────────────┐
 *               │                     │  │  ❌ NO JWT/ID    │  │  ✅ HAS JWT+ID   │
 *               │                     │  │  (First/Logout)  │  │  (Page reload)   │
 *               │                     │  └────────┬─────────┘  └────────┬─────────┘
 *               │                     │           │                     │
 *               │                     │           ▼                     ▼
 *               │                     │  ┌──────────────────┐  ┌──────────────────┐
 *               │                     │  │   START NEW      │  │   RESUME         │
 *               │                     │  │ • getUnauth JWT  │  │ • getContinuity  │
 *               │                     │  │ • Create convo   │  │ • Fetch history  │
 *               │                     │  │ • Save JWT+ID    │  │ • Restore msgs   │
 *               │                     │  │ • EventSource    │  │ • EventSource    │
 *               │                     │  │ • state='init'   │  │ • state='init'   │
 *               │                     │  └────────┬─────────┘  └────────┬─────────┘
 *               │                     │           │                     │
 *               │                     │           │         ┌───────────┴────────┐
 *               │                     │           │         │                    │
 *               │                     │           │         ▼                    ▼
 *               │                     │           │  ┌──────────────┐  ┌──────────────┐
 *               │                     │           │  │Server:ACTIVE │  │Server:ENDED  │
 *               │                     │           │  └──────┬───────┘  └──────┬───────┘
 *               │                     │           │         │                 │
 *               └─────────────────────┴───────────┴─────────┴─────────────────┘
 *                                                   │
 *                                     ┌─────────────▼──────────────┐
 *                                     │   SHOW IN UI:              │
 *                                     │ • Display messages         │
 *                                     │ • Show connection status   │
 *                                     │ • Enable/disable input     │
 *                                     │ ⚡ INSTANT (if 'init')     │
 *                                     │ 🔄 RESTORED (if reload)    │
 *                                     └─────────────┬──────────────┘
 *                                                   │
 *                                     ┌─────────────▼──────────────┐
 *                                     │   USER CLOSES DIALOG       │
 *                                     └─────────────┬──────────────┘
 *                                                   │
 *                              ┌────────────────────┴─────────────────────┐
 *                              │                                          │
 *                              ▼                                          ▼
 *                   ┌────────────────────┐                   ┌────────────────────┐
 *                   │ Status: ACTIVE     │                   │ Status: ENDED      │
 *                   │ ────────────────── │                   │ ────────────────── │
 *                   │ • Keep state       │                   │ • Reset state!     │
 *                   │ • Keep JWT+ID      │                   │ • Clear JWT+ID     │
 *                   │ • Keep messages    │                   │ • Clear messages   │
 *                   │ • state='init' ✅  │                   │ • state='not-start'│
 *                   │                    │                   │ • Next open = NEW  │
 *                   │ Reopen = instant!  │                   │                    │
 *                   └────────────────────┘                   └────────────────────┘
 * 
 * STATE MACHINE (salesforce.sessionInitState in Redux):
 * ┌─────────────────┬──────────────────────────────────────────────────────┐
 * │ State           │ Meaning                                              │
 * ├─────────────────┼──────────────────────────────────────────────────────┤
 * │ 'not-started'   │ Initial state (page reload / first open)             │
 * │                 │ → Need to check localStorage and init                │
 * ├─────────────────┼──────────────────────────────────────────────────────┤
 * │ 'initializing'  │ Async operation in progress                          │
 * │                 │ → Prevent duplicate/concurrent calls (guard)         │
 * ├─────────────────┼──────────────────────────────────────────────────────┤
 * │ 'initialized'   │ Successfully loaded this browser session             │
 * │                 │ → Skip re-init on component unmount/remount          │
 * │                 │ → Persists in Redux across close/reopen             │
 * └─────────────────┴──────────────────────────────────────────────────────┘
 * 
 * DATA LAYERS:
 * - salesforce.sessionInitState (Redux): Session state machine, persists across component unmount
 * - salesforceConversationStatus (Redux): Server state (NOT_STARTED/ACTIVE/ENDED)
 * - localStorage: JWT, conversationId (survives page reload)
 * - Redux State: messages, status, connection (cleared on reload, intact on close/reopen)
 * - EventSource: Real-time connection (alive on reopen, reconnects on reload)
 * 
 * KEY DECISION POINTS:
 * 1. ⚡ Check state: 'initialized'? → SKIP (instant close/reopen, even with unmount)
 * 2. ⏳ Check state: 'initializing'? → SKIP (prevent race conditions)
 * 3. 🔄 Check localStorage → Resume or Start New
 * 4. ✅ Set state = 'initialized' after success
 * 
 * SCENARIOS:
 * - 🚀 Close/reopen dialog (ACTIVE) = state='initialized' in Redux, SKIP all (instant)
 * - ⚠️ Close dialog (ENDED) = Reset state to 'not-started', clear localStorage/Redux
 * - 🆕 Reopen after ENDED = state='not-started', create NEW conversation
 * - 🔄 Page reload = state='not-started' (Redux cleared), check localStorage
 * - 🆕 First time = state='not-started', no localStorage, start new
 * ============================================================================
 */

const SupportChatList = ({
  authId,
  chatUsers,
  locale,
  messages,
  onClose,
  routingAttributes = {},
  userContext = null // User identity from Cognito { userId, email, name }
}) => {
  const { messages: intlMessages } = useIntl();
  const localizedMessages = messages || intlMessages;
  const dispatch = useDispatch();

  // Redux state
  const {
    items = [],
    chat,
    loading = false,
    openModal = false,
    error = null,
    salesforce = {}
  } = useSelector(state => state.supportMessageListRedux) || {};

  // Salesforce state
  const salesforceIsInitialized = useSelector(selectSalesforceIsInitialized);
  const salesforceIsReady = useSelector(selectSalesforceIsReady);
  const salesforceSessionInitState = useSelector(selectSalesforceSessionInitState); // From Redux, persists across unmount
  const salesforceConversationStatus = useSelector(selectSalesforceConversationStatus);
  const salesforceConnectionStatus = useSelector(selectSalesforceConnectionStatus);
  const salesforceCanSendMessage = useSelector(selectSalesforceCanSendMessage);
  const hasAgentTimedOut = useSelector(selectHasAgentTimedOut);
  const isWaitingForAgent = useSelector(selectIsWaitingForAgent);
  const salesforceUserContext = useSelector(selectSalesforceUserContext);
  const isNetworkOnline = useSelector(selectIsNetworkOnline);

  // Set user context in Redux when component mounts (for authenticated users)
  // If userContext prop is not provided, fetch from Cognito localStorage
  useEffect(() => {
    const initUserContext = async () => {
      let contextToSet = userContext;

      // If userContext prop is not provided, try to get from Cognito tokens in localStorage
      if (!contextToSet || !contextToSet.userId) {
        try {
          const idToken = localStorage.getItem('IdToken');
          if (idToken) {
            // Decode JWT to get user info
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            contextToSet = {
              userId: payload.sub,  // Cognito sub claim
              email: payload.email || '',
              name: payload.name || payload['cognito:username'] || payload.email || ''
            };
            console.log('[Salesforce:Component] User context retrieved from Cognito:', contextToSet);
          }
        } catch (error) {
          console.error('[Salesforce:Component] Failed to retrieve user context from Cognito:', error);
        }
      }

      if (contextToSet && contextToSet.userId) {
        dispatch(setSalesforceUserContext(contextToSet));
      } else {
        console.warn('[Salesforce:Component] No user context available - authenticated chat may fail');
      }
    };

    initUserContext();
  }, [userContext, dispatch]);

  // Check if Salesforce is enabled (ensure boolean value)
  const salesforceEnabled = Boolean(isSalesforceEnabled());

  const isTypingTimeoutRef = useRef(null);

  // Salesforce initialization
  const initializeSalesforceService = useCallback(async () => {
    if (!salesforceEnabled || salesforceIsInitialized) return;

    try {
      // Pass the configuration to ensure it's properly initialized
      await dispatch(initializeSalesforce(SALESFORCE_CONFIG)).unwrap();
    } catch (error) {
      // Failed to initialize Salesforce service
    }
  }, [dispatch, salesforceEnabled, salesforceIsInitialized]);

  // Start Salesforce conversation
  const startSalesforceConversationWithRouting = useCallback(async () => {
    if (!salesforceIsInitialized ||
      salesforceConversationStatus === CONVERSATION_STATUS.ACTIVE ||
      salesforceConversationStatus === CONVERSATION_STATUS.WAITING_FOR_AGENT) {
      return;
    }

    // ⚠️ CRITICAL: Ensure user context is available for authenticated sessions
    if (!salesforceUserContext || !salesforceUserContext.userId) {
      console.error('[Salesforce:Component] Cannot start conversation: User context is required');
      return;
    }

    try {
      const routing = getRoutingAttributes(salesforceUserContext, {
        ...routingAttributes,
        [ROUTING_ATTRIBUTES.SOURCE]: 'Web_Application',
        [ROUTING_ATTRIBUTES.USER_ID]: authId,
        [ROUTING_ATTRIBUTES.LOCALE]: locale
      });
      
      // Pass userContext and routing to Redux thunk
      await dispatch(startSalesforceConversation({ 
        userContext: salesforceUserContext, 
        routingAttributes: routing 
      })).unwrap();
    } catch (error) {
      console.error('[Salesforce:Component] Failed to start Salesforce conversation:', error);
    }
  }, [dispatch, salesforceIsInitialized, salesforceUserContext, routingAttributes, authId, locale, salesforceConversationStatus]);

  // Helper function to start new conversation and mark as initialized
  const startNewConversationAndMarkInitialized = useCallback(async () => {
    await startSalesforceConversationWithRouting();
    dispatch(setSalesforceSessionInitState(SESSION_INIT_STATE.INITIALIZED));
  }, [startSalesforceConversationWithRouting, dispatch]);

  // Helper function to clear conversation and start new
  const clearAndStartNewConversation = useCallback(async () => {
    const { clearConversationId } = await import('./services/webStorageService');
    clearConversationId();
    await startNewConversationAndMarkInitialized();
  }, [startNewConversationAndMarkInitialized]);

  /**
   * Initialize or Resume Salesforce Conversation
   * 
   * FLOW (USER-ID BASED - Cross-Device Support):
   * 
   * 1️⃣ CHECK: Initialization state (from Redux - persists across unmount/remount)
   *    ✅ 'initialized' → SKIP (conversation already loaded this session)
   *       - User just closed and reopened dialog (component remounted)
   *       - Redux has all data (messages, status, connection)
   *       - EventSource still alive and connected
   *       - NO API calls needed, instant display
   * 
   *    ⏳ 'initializing' → SKIP (already in progress, prevent duplicate)
   *       - Async operation currently running
   *       - Prevents race conditions
   * 
   *    ❌ 'not-started' → CONTINUE to userId-based lookup
   * 
   * 2️⃣ AUTHENTICATE & LOOKUP BY USER ID:
   *    - Get Cognito IdToken (contains sub claim = userId)
   *    - Exchange for Salesforce Messaging Access Token
   *    - Call /end-user endpoint to check if user has active conversation
   *      (Salesforce uses JWT sub claim to identify verified user)
   * 
   *    ✅ HAS active conversation → RESUME
   *       - Use conversationId from /end-user response
   *       - Fetch conversation history
   *       - Works across devices (same Cognito user = same conversation)
   * 
   *    ❌ NO active conversation → START NEW
   *       - Create new conversation with routing attributes
   *       - Pass user profile (email, name) via routingAttributes
   * 
   * KEY INSIGHT (Cross-Device):
   * - Identity is established via Cognito sub (JWT claim)
   * - Same Cognito user on different devices = same verified user in Salesforce
   * - No need to store conversationId locally for cross-device support
   * - /end-user endpoint returns active conversation for authenticated user
   */
  // Check for existing conversation and resume or start new
  const initializeConversation = useCallback(async () => {
    if (!salesforceIsInitialized) return;

    // 🚀 OPTIMIZATION: If already initialized this session, SKIP
    if (salesforceSessionInitState === SESSION_INIT_STATE.INITIALIZED) {
      return;
    }

    // ⏳ GUARD: Prevent duplicate initialization attempts
    if (salesforceSessionInitState === SESSION_INIT_STATE.INITIALIZING) {
      return;
    }

    // ⚠️ CRITICAL: Ensure user context is available for authenticated sessions
    if (!salesforceUserContext || !salesforceUserContext.userId) {
      console.error('[Salesforce:Component] Cannot initialize: User context is required');
      return;
    }

    try {
      // Set state to 'initializing'
      dispatch(setSalesforceSessionInitState(SESSION_INIT_STATE.INITIALIZING));

      // ✅ UX IMPROVEMENT: Set status to CONNECTING sooner for better user experience
      // This shows "Connecting to support..." instead of "Initializing..." during the process
      dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.CONNECTING));

      // Start conversation with userId-based lookup
      // salesforceService.startConversation will:
      // 1. Get authenticated Salesforce token using Cognito JWT
      // 2. Call /end-user to check for existing conversation (by userId via JWT sub)
      // 3. If exists: resume with history
      // 4. If not: create new with routing attributes
      await startNewConversationAndMarkInitialized();
    } catch (error) {
      // Fallback: start new conversation
      console.error('[Salesforce:Component] Initialization error:', error);
      await startNewConversationAndMarkInitialized();
    }
  }, [dispatch, salesforceIsInitialized, salesforceSessionInitState, salesforceUserContext, startNewConversationAndMarkInitialized]);

  const initializeNetworkMonitoring = useCallback(() => {
    try {
      // Start enhanced network monitoring with all strategies
      networkMonitorService.startMonitoring({
        pollingEndpoint: '/manifest.json',
        pollingInterval: 5000,
      });

      console.log('[SupportChat] Enhanced network monitoring initialized');

      // Add listener for status changes
      networkMonitorService.addListener(SUPPORT_CHAT_NETWORK_LISTENER_ID, (status) => {
        const isOnline = status === CONNECTION_STATUS.CONNECTED;
        console.log(`[SupportChat] Network status changed: ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
        dispatch(setIsNetworkOnline(isOnline));
      });
    } catch (error) {
      console.error('[NetworkMonitor] Initialization error:', error);
    }
  }, [dispatch]);

  // Handle sending messages (supports both Salesforce and regular)
  const handleSendMessage = useCallback(async (messageData) => {
    if (salesforceEnabled && salesforceCanSendMessage) {
      try {
        await dispatch(sendSalesforceMessage(messageData.message)).unwrap();
      } catch (error) {
        // Failed to send Salesforce message, fallback to regular message handling
        dispatch(chatMessageSend({
          ...messageData,
          chatId: chat?.id,
          type: 'fallback'
        }));
      }
    } else {
      // Send via regular chat
      dispatch(chatMessageSend({
        ...messageData,
        chatId: chat?.id
      }));
    }
  }, [dispatch, salesforceEnabled, salesforceCanSendMessage, chat?.id]);

  // Handle message status updates
  const handleUpdateMessageStatus = useCallback((messageIds, status) => {
    dispatch(chatMessageUpdateStatus({
      ids: messageIds,
      status: status || 'Read'
    }));
  }, [dispatch]);

  // Handle typing indicator
  const handleUserTyping = useCallback((chatId, userId, isTyping = true) => {
    // Send typing indicator to regular chat
    dispatch(setChatUserWriting({
      chatId,
      userId
    }));

    // Send typing indicator via Salesforce if enabled, ready, and conversation is active
    if (salesforceEnabled &&
      salesforceCanSendMessage &&
      salesforceConversationStatus === CONVERSATION_STATUS.ACTIVE) {
      dispatch(sendSalesforceTypingIndicator(isTyping));

      // Clear typing after 3 seconds
      if (isTyping) {
        clearTimeout(isTypingTimeoutRef.current);
        isTypingTimeoutRef.current = setTimeout(() => {
          dispatch(sendSalesforceTypingIndicator(false));
        }, 3000);
      }
    }
  }, [dispatch, salesforceEnabled, salesforceCanSendMessage, salesforceConversationStatus]);

  // Handle close
  const handleClose = useCallback((isClosingViaBackButton = false) => {
    // Clear typing timeout
    clearTimeout(isTypingTimeoutRef.current);

    // ✅ CRITICAL: Always stop any running timer when modal closes
    // This prevents duplicate timers when user reopens modal
    // Timer will be restarted if needed when modal reopens based on conversation state
    if (isWaitingForAgent) {
      dispatch(stopAgentWaitTimerAsync());
    }

    // ⚠️ CRITICAL DISTINCTION:
    // - Back button (isClosingViaBackButton=true): Preserve state, user might return
    // - Close button X (isClosingViaBackButton=false): Clear ENDED/ERROR states for fresh start

    const shouldClearState = !isClosingViaBackButton && (
      salesforceConversationStatus === CONVERSATION_STATUS.ENDED ||
      salesforceConversationStatus === CONVERSATION_STATUS.ERROR ||
      salesforceConversationStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE ||
      salesforceConversationStatus === CONVERSATION_STATUS.NO_AGENTS_FOUND ||
      hasAgentTimedOut
    );

    if (shouldClearState) {
      // Reset session init state so next open will create new conversation
      dispatch(setSalesforceSessionInitState(SESSION_INIT_STATE.NOT_STARTED));

      // Clear localStorage (conversationId, JWT, start time) to ensure fresh start
      import('./services/webStorageService').then(({ clearConversationId, clearJwt, clearConversationStartTime }) => {
        clearConversationId();
        clearJwt();
        clearConversationStartTime();
      });

      // Clear conversation data from Redux
      dispatch(setItems([]));
      dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.NOT_STARTED));

      // Reset agent wait state for fresh start
      dispatch(resetAgentWaitState());
    }

    // ⚠️ IMPORTANT: Do NOT end ACTIVE conversation when closing modal!
    // The conversation should stay active so users can reopen the modal and resume.
    // Conversations should only end when:
    // 1. Agent explicitly closes the conversation (CONVERSATION_CLOSE_CONVERSATION event)
    // 2. Work is closed (CONVERSATION_ROUTING_WORK_RESULT with workType: 'Closed')
    // 3. Agent is removed (CONVERSATION_PARTICIPANT_CHANGED with agent removal)

    // Just close the modal UI
    dispatch(closeChat());
    onClose?.();
  }, [dispatch, onClose, salesforceConversationStatus, hasAgentTimedOut, isWaitingForAgent]);

  // Initialize chat and Salesforce when component mounts
  useEffect(() => {
    // Initialize Salesforce if enabled
    if (salesforceEnabled) {
      initializeSalesforceService();
    }

    // Set initial chat if not set
    if (!chat) {
      dispatch(setChat({
        id: 'support',
        name: localizedMessages?.['support_chat_title'] || 'Support Chat',
        type: 'support',
        salesforceEnabled
      }));
    }

    // Open chat modal
    dispatch(openChat());

    // Set initial items if empty
    if (!items || !items.length) {
      dispatch(setItems([]));
    }
  }, [dispatch, chat, items?.length, localizedMessages, salesforceEnabled]);

  // Cleanup on unmount - preserve conversation state for back navigation
  useEffect(() => {
    return () => {
      // Note: We preserve conversation state in Redux for back navigation
      // DELETE conversation is handled in handleClose() when X button is clicked, not on unmount
      console.log('[Salesforce:Component] Component unmounting, preserving state for back navigation');
    };
  }, []);

  // Start conversation when Salesforce is initialized or when session needs re-initialization
  useEffect(() => {
    if (salesforceIsInitialized && openModal) {
      initializeConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesforceIsInitialized, openModal, salesforceSessionInitState]);

  // Start network monitoring: ALWAYS start when modal opens, regardless of Salesforce init status
  // This ensures we can detect network restoration even if Salesforce failed to init due to offline
  useEffect(() => {
    if (!openModal || !salesforceEnabled) return;
    
    // Start monitoring if not already monitoring
    if (!networkMonitorService.isMonitoring) {
      initializeNetworkMonitoring();
    }
    
    // Cleanup when modal closes
    return () => {
      // Don't stop monitoring on unmount - let it continue for back navigation
    };
  }, [openModal, salesforceEnabled, initializeNetworkMonitoring]);

  // ✅ FIX: Handle network restoration - re-initialize conversation when network comes back online
  // Subscribe DIRECTLY to networkMonitorService to ensure we catch network changes
  // even if the Redux listener wasn't set up properly
  useEffect(() => {
    if (!openModal || !salesforceEnabled) {
      return;
    }
    
    const RECONNECT_LISTENER_ID = 'support-chat-reconnect-listener';
    
    // Handler for network status changes
    const handleNetworkChange = (status) => {
      const isOnline = status === CONNECTION_STATUS.CONNECTED;
      console.log(`[Salesforce:Component] Direct network listener - status: ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
      
      if (!isOnline) {
        // Going offline - just update state
        dispatch(setIsNetworkOnline(false));
        return;
      }
      
      // Network is now ONLINE
      dispatch(setIsNetworkOnline(true));
      
      // ✅ SCENARIO 1: Salesforce failed to initialize because user opened chat while offline
      if (!salesforceIsInitialized) {
        console.log('[Salesforce:Component] Network online but Salesforce not initialized - retrying initialization');
        initializeSalesforceService();
        return;
      }
      
      // ✅ SCENARIO 2: Conversation is in ERROR state (opened while offline)
      if (salesforceConversationStatus === CONVERSATION_STATUS.ERROR) {
        console.log('[Salesforce:Component] Network online, conversation in ERROR - resetting to re-initialize');
        dispatch(setSalesforceSessionInitState(SESSION_INIT_STATE.NOT_STARTED));
        dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.NOT_STARTED));
        return;
      }
      
      // ✅ FIX: Removed SCENARIO 3 (isStuckInInitializing check) - it was causing a loop!
      // CONNECTING is a normal transient state, not a "stuck" state.
    };
    
    // Subscribe to network monitor service directly
    // This ensures we catch network changes even if the main listener wasn't set up
    networkMonitorService.addListener(RECONNECT_LISTENER_ID, handleNetworkChange);
    
    // Also check current status immediately on mount/when dependencies change
    const currentStatus = networkMonitorService.getStatus();
    const isCurrentlyOnline = currentStatus === CONNECTION_STATUS.CONNECTED;
    
    // If network is online but we're in a bad state, trigger recovery
    if (isCurrentlyOnline) {
      const isInErrorState = salesforceConversationStatus === CONVERSATION_STATUS.ERROR;
      const needsInitialization = !salesforceIsInitialized;
      
      if (needsInitialization) {
        console.log('[Salesforce:Component] Mount check: Network online but Salesforce not initialized - initializing');
        initializeSalesforceService();
      } else if (isInErrorState) {
        console.log('[Salesforce:Component] Mount check: Network online but in ERROR state - resetting');
        dispatch(setSalesforceSessionInitState(SESSION_INIT_STATE.NOT_STARTED));
        dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.NOT_STARTED));
      }
      // ✅ FIX: Removed isStuckInInitializing check here - it was causing a loop!
      // CONNECTING is a normal state during initialization, not a "stuck" state.
      // The previous logic would reset sessionInitState when status was CONNECTING,
      // which triggered initializeConversation() again, creating an infinite loop.
    }
    
    // Cleanup listener on unmount or when dependencies change
    return () => {
      networkMonitorService.removeListener(RECONNECT_LISTENER_ID);
    };
  }, [openModal, salesforceEnabled, salesforceIsInitialized, salesforceSessionInitState, salesforceConversationStatus, dispatch, initializeSalesforceService]);

  // Update Salesforce status periodically
  useEffect(() => {
    if (!salesforceEnabled) return;

    const statusInterval = setInterval(() => {
      dispatch(updateSalesforceStatus());
    }, 30000); // Update every 30 seconds

    return () => clearInterval(statusInterval);
  }, [dispatch, salesforceEnabled]);

  // Simple modal reopen logic - check if we need to resume or restart timer
  const prevOpenModalRef = useRef(openModal);
  useEffect(() => {
    // Only act when modal reopens (was closed, now opening)
    if (salesforceEnabled && openModal && !prevOpenModalRef.current && salesforceIsInitialized) {
      // If no timer is currently running, decide what to do based on conversation state
      if (!isWaitingForAgent) {
        // ✅ CRITICAL: Check if this is a back-button reopen (sessionInitState is 'initialized')
        // vs. a fresh open after close button (sessionInitState is 'not-started')
        const isReopeningAfterBack = salesforceSessionInitState === SESSION_INIT_STATE.INITIALIZED;
        
        if (isReopeningAfterBack && (
            salesforceConversationStatus === CONVERSATION_STATUS.ENDED ||
            salesforceConversationStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE ||
            salesforceConversationStatus === CONVERSATION_STATUS.NO_AGENTS_FOUND
          )) {
          // Case 1: Reopening after back button with terminal/waiting states → Keep showing current state, don't restart
          // User clicked back button, conversation state was preserved, just show the current state
          console.log('[Salesforce:Container] Reopening after back button - preserving state:', salesforceConversationStatus);
        } else if (salesforceConversationStatus === CONVERSATION_STATUS.ENDED ||
          hasAgentTimedOut ||
          salesforceConversationStatus === CONVERSATION_STATUS.ERROR ||
          salesforceConversationStatus === CONVERSATION_STATUS.AGENT_UNAVAILABLE ||
          salesforceConversationStatus === CONVERSATION_STATUS.NO_AGENTS_FOUND) {
          // Case 2: Close button was pressed (state cleared) → Start completely fresh
          dispatch(resetAgentWaitState());
          initializeConversation();
        } else if (salesforceConversationStatus === CONVERSATION_STATUS.CONNECTING) {
          // Case 3: Conversation exists but no timer → Resume waiting for agent
          dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.WAITING_FOR_AGENT));
          dispatch(startAgentWaitTimerAsync({ timeout: 30 }));
        } else if (salesforceConversationStatus === CONVERSATION_STATUS.ACTIVE) {
          // Case 4: Active conversation with agent → Just restore, no timer needed
        }
      }
    }

    // Update ref
    prevOpenModalRef.current = openModal;
  }, [dispatch, salesforceEnabled, openModal, salesforceIsInitialized, salesforceConversationStatus, salesforceSessionInitState, isWaitingForAgent, hasAgentTimedOut, initializeConversation]);

  // Auto-start timer when fresh conversation reaches CONNECTING status
  useEffect(() => {
    // Only for Salesforce conversations that are CONNECTING but don't have a timer running
    if (salesforceEnabled &&
      salesforceIsInitialized &&
      salesforceConversationStatus === CONVERSATION_STATUS.CONNECTING &&
      !isWaitingForAgent &&
      !hasAgentTimedOut) {

      // ✅ CRITICAL: Add small delay to allow resumeSalesforceConversation to complete
      const checkAndStartTimer = () => {
        const hasExistingMessages = items && items.length > 0;

        if (hasExistingMessages) {
          // Don't start timer, conversation already has messages - set directly to ACTIVE
          dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.ACTIVE));
        } else if (!loading) {
          // Only start timer if not loading (resumeSalesforceConversation has completed)
          // Start timer and set status to WAITING_FOR_AGENT
          dispatch(setSalesforceConversationStatus(CONVERSATION_STATUS.WAITING_FOR_AGENT));
          dispatch(startAgentWaitTimerAsync({ timeout: 30 }));
        }
      };

      // Small delay to allow async operations to complete
      const timeoutId = setTimeout(checkAndStartTimer, TIMER_CONFIG.AUTO_TIMER_DELAY);
      return () => clearTimeout(timeoutId);
    }
  }, [dispatch, salesforceEnabled, salesforceIsInitialized, salesforceConversationStatus, isWaitingForAgent, hasAgentTimedOut, items, loading]);

  // Create Salesforce status object for the component
  const salesforceStatus = salesforceEnabled ? {
    isInitialized: salesforceIsInitialized,
    isReady: salesforceIsReady,
    conversationStatus: salesforceConversationStatus,
    connectionStatus: salesforceConnectionStatus,
    canSendMessage: salesforceCanSendMessage,
    isTyping: salesforce.participantInfo?.isTyping || false,
    agentWaitTimer: salesforce.agentWaitTimer
  } : null;

  return (
    <SupportMessageList
      authId={authId}
      chatUsers={chatUsers}
      items={items}
      chat={chat}
      loading={loading}
      error={error}
      openModal={openModal}
      messages={localizedMessages}
      onClose={handleClose}
      onSendMessage={handleSendMessage}
      onUpdateMessageStatus={handleUpdateMessageStatus}
      onUserStatusWriting={handleUserTyping}
      salesforceEnabled={salesforceEnabled}
      salesforceStatus={salesforceStatus}
      locale={locale}
      routingAttributes={routingAttributes}
    />
  );
};

export default SupportChatList;
